// Settings Feature Type Definitions
// ============================================================================
// Constants
// ============================================================================
const PERMISSIONS = [
    // Study & Image Management
    {
        key: 'view_studies',
        label: 'View Studies',
        tooltip: 'View studies in the system',
        category: 'study_management',
    },
    {
        key: 'import_data',
        label: 'Import Data',
        tooltip: 'Import images from CD, email, USB drive or folder',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'edit_study',
        label: 'Edit Study',
        tooltip: 'Edit study metadata (description, patient name, date)',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'delete_studies',
        label: 'Delete Studies',
        tooltip: 'Delete studies',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'merge_studies',
        label: 'Merge Studies',
        tooltip: "Fold a patient's sibling studies into one",
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'anonymize_studies',
        label: 'Anonymize Studies',
        tooltip: 'Create a de-identified copy of a study as a new study',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'export_media',
        label: 'Export Media',
        tooltip: 'Export media files',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    {
        key: 'export_data',
        label: 'Export Data',
        tooltip: 'Export DICOM files to folder, USB or CD/DVD',
        category: 'study_management',
        dependencies: ['view_studies'],
    },
    // Image Viewing & Tools
    {
        key: 'edit_data',
        label: 'Edit Data',
        tooltip: 'Edit data in the system',
        category: 'viewing_tools',
        dependencies: ['view_studies'],
    },
    {
        key: 'customize_toolbar',
        label: 'Customize Toolbar',
        tooltip: 'Customize viewer toolbar layout and available tools',
        category: 'viewing_tools',
        dependencies: ['view_studies'],
    },
    // Reporting
    {
        key: 'create_report',
        label: 'Create Report',
        tooltip: 'Create and edit reports not sent for consultation',
        category: 'reporting',
        dependencies: ['view_studies'],
    },
    {
        key: 'edit_finalized_reports',
        label: 'Edit Finalized Reports',
        tooltip: 'Modify reports after signing (with audit trail)',
        category: 'reporting',
        dependencies: ['create_report'],
    },
    {
        key: 'teleradiology_data',
        label: 'Teleradiology Data',
        tooltip: 'View all teleradiology data (drafts and reports)',
        category: 'reporting',
        dependencies: ['view_studies'],
    },
    // Sharing & Collaboration
    {
        key: 'send_email',
        label: 'Send Email',
        tooltip: 'Send studies, images, and reports via email',
        category: 'sharing',
        dependencies: ['view_studies'],
    },
    {
        key: 'send_dicom',
        label: 'Send DICOM',
        tooltip: 'Send studies and images to DICOM peers',
        category: 'sharing',
        dependencies: ['view_studies'],
    },
    {
        key: 'send_teleradiology',
        label: 'Send Teleradiology',
        tooltip: 'Create new teleradiology requests',
        category: 'sharing',
        dependencies: ['view_studies'],
    },
    // Configuration & Integration
    {
        key: 'server_administration',
        label: 'Server Administration',
        tooltip: 'Access to all advanced system configuration options',
        category: 'configuration',
    },
    {
        key: 'view_schedule',
        label: 'View Schedule',
        tooltip: 'Read-only access to view the schedule',
        category: 'configuration',
    },
    {
        key: 'manage_schedule',
        label: 'Manage Schedule',
        tooltip: 'Add/edit/move/delete scheduled items',
        category: 'configuration',
        dependencies: ['view_schedule'],
    },
    {
        key: 'manage_modality_worklist',
        label: 'Manage Modality Worklist',
        tooltip: 'Configure what studies appear on imaging devices',
        category: 'configuration',
    },
    {
        key: 'manage_dicom_nodes',
        label: 'Manage DICOM Nodes',
        tooltip: 'Add/edit DICOM destinations and peers',
        category: 'configuration',
    },
    {
        key: 'configure_auto_routing',
        label: 'Configure Auto Routing',
        tooltip: 'Set up automatic study distribution rules',
        category: 'configuration',
    },
    // User Administration
    {
        key: 'user_administration',
        label: 'User Administration',
        tooltip: 'Add/edit/delete user accounts in Keystone',
        category: 'user_admin',
    },
    {
        key: 'asteris_administrator',
        label: 'Asteris Administrator',
        tooltip: 'Full administrative access to Asteris',
        category: 'user_admin',
    },
    {
        key: 'non_veterinarian',
        label: 'Non-Veterinarian',
        tooltip: 'Non-veterinarian user designation',
        category: 'user_admin',
    },
];
export const ROLE_BADGE_COLORS = {
    gray: { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-500/30' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
    green: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30' },
    yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30' },
    red: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30' },
    orange: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30' },
    cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30' },
    teal: { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-500/30' },
};
export const SYSTEM_ROLES = [
    {
        id: 'super-admin',
        name: 'Super Admin',
        description: 'Full system access',
        color: 'red',
        isSystem: true,
        permissions: PERMISSIONS.map((p) => p.key),
    },
    {
        id: 'clinic-admin',
        name: 'Clinic Admin',
        description: 'Clinic-level administration',
        color: 'purple',
        isSystem: true,
        permissions: [
            'view_studies',
            'import_data',
            'edit_study',
            'delete_studies',
            'merge_studies',
            'export_media',
            'export_data',
            'anonymize_studies',
            'edit_data',
            'customize_toolbar',
            'create_report',
            'send_email',
            'send_dicom',
            'view_schedule',
            'manage_schedule',
            'user_administration',
        ],
    },
    {
        id: 'pacs-admin',
        name: 'PACS Admin',
        description: 'PACS system management',
        color: 'blue',
        isSystem: true,
        permissions: [
            'view_studies',
            'import_data',
            'delete_studies',
            'merge_studies',
            'export_data',
            'anonymize_studies',
            'server_administration',
            'manage_modality_worklist',
            'manage_dicom_nodes',
            'configure_auto_routing',
        ],
    },
    {
        id: 'radiologist',
        name: 'Radiologist',
        description: 'Full diagnostic capabilities',
        color: 'green',
        isSystem: true,
        permissions: [
            'view_studies',
            'edit_data',
            'customize_toolbar',
            'create_report',
            'edit_finalized_reports',
            'teleradiology_data',
            'send_email',
            'send_teleradiology',
        ],
    },
    {
        id: 'technician',
        name: 'Technician',
        description: 'Study management and upload',
        color: 'cyan',
        isSystem: true,
        permissions: [
            'view_studies',
            'import_data',
            'edit_study',
            'export_media',
            'export_data',
            'anonymize_studies',
            'send_dicom',
            'view_schedule',
        ],
    },
    {
        id: 'view-only',
        name: 'View Only',
        description: 'Read-only access',
        color: 'gray',
        isSystem: true,
        permissions: ['view_studies', 'view_schedule'],
    },
];
